"use client"

import { v4 as uuidv4 } from "uuid"
import { ControlledModal, toast } from "@/components"
import { IconButton, Image } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import { RemoveUserProfileAvatarDocument, UpdateUserProfileAvatarDocument } from "@/gql/graphql"
import { useSession } from "@/hooks/useSession"
import { getCroppedImg } from "@/utils/cropImage"
import { useMutation } from "@apollo/client"
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline"
import clsx from "clsx"
import { ChangeEvent, FC, useCallback, useMemo, useRef, useState } from "react"
import Cropper, { Area } from "react-easy-crop"

const EditProfileImageModalFragment = graphql(`
  fragment EditProfileImageModal on User {
    id
    profile {
      id
      avatar390w: avatarUrl(width: 390)
    }
  }
`)

graphql(`
  mutation UpdateUserProfileAvatar($file: Upload!, $id: ID!) {
    updateUserProfileAvatar(input: { avatar: $file, id: $id }) {
      userProfile {
        id
        avatarUrl72w: avatarUrl(width: 72)
        avatarUrl120w: avatarUrl(width: 120)
        avatarUrl144w: avatarUrl(width: 144)
        avatarUrl240w: avatarUrl(width: 240)
        avatarUrl390w: avatarUrl(width: 390)
      }
      errors {
        field
        messages
      }
    }
  }
`)

graphql(`
  mutation RemoveUserProfileAvatar($id: ID!) {
    removeUserProfileAvatar(input: { id: $id }) {
      userProfile {
        id
        avatarUrl72w: avatarUrl(width: 72)
        avatarUrl120w: avatarUrl(width: 120)
        avatarUrl144w: avatarUrl(width: 144)
        avatarUrl240w: avatarUrl(width: 240)
        avatarUrl390w: avatarUrl(width: 390)
      }
      ok
    }
  }
`)

interface Props {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  user: FragmentType<typeof EditProfileImageModalFragment> | null | undefined
}

enum UiState {
  NoImage,
  ExistingImage,
  LocalImage,
}

export const EditProfileImageModal: FC<Props> = ({ isOpen, onOpenChange, ...props }) => {
  const user = useFragment(EditProfileImageModalFragment, props.user)
  const { update: updateSession, data: session } = useSession()
  const [updateProfilePicture, { loading: uploadLoading }] = useMutation(UpdateUserProfileAvatarDocument)
  const [removeProfilePicture, { loading: removeLoading }] = useMutation(RemoveUserProfileAvatarDocument)

  const [localImage, setLocalImage] = useState("")

  const existingImage = user?.profile?.avatar390w

  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedImage, setCroppedImage] = useState<Blob | null>(null)
  const hiddenInput = useRef<HTMLInputElement>(null)

  const resetLocalImage = () => {
    setLocalImage("")
    setCroppedImage(null)
    setCrop({ x: 0, y: 0 })
    setZoom(1)
  }

  const onDownloadCropClick = async () => {
    if (!croppedImage || !user?.id) return
    const uuid = uuidv4()
    const file = new File([croppedImage], `${uuid}.jpeg`)

    const res = await updateProfilePicture({
      variables: { file: file, id: user?.id! },
    })

    if (res.data?.updateUserProfileAvatar?.errors?.length === 0) {
      toast.success("Profile picture updated")
      updateSession({ ...session, user: { ...session?.user, profile: res?.data.updateUserProfileAvatar.userProfile } })
      resetLocalImage()
      onOpenChange(false)
    } else {
      toast.error("Profile picture not updated")
    }
  }

  const handleRemoveExisting = () => {
    const profileId = user?.profile?.id
    if (!profileId) return
    removeProfilePicture({
      variables: { id: profileId },
      onCompleted(data) {
        if (data.removeUserProfileAvatar?.ok) {
          updateSession({ ...session, user: { ...session?.user, profile: data.removeUserProfileAvatar.userProfile } })
        }
      },
    })
  }

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setLocalImage(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const uiState = useMemo(() => {
    if (localImage) {
      return UiState.LocalImage
    } else if (existingImage) {
      return UiState.ExistingImage
    }
    return UiState.NoImage
  }, [localImage, existingImage])

  const onCropComplete = useCallback(
    async (_: Area, croppedAreaPixels: Area) => {
      // setCroppedAreaPixels(croppedAreaPixels)
      try {
        const croppedImage = await getCroppedImg(localImage, croppedAreaPixels)
        setCroppedImage(croppedImage)
      } catch (e) {
        console.error(e)
      }
    },
    [localImage]
  )

  return (
    <ControlledModal
      open={isOpen}
      onOpenChange={onOpenChange}
      header={{ title: "Choose a profile picture", variant: "default", fixed: true }}
      body={
        <div className="flex flex-1 flex-col overflow-y-scroll px-5 md:px-8">
          <div className={clsx("my-10", uiState !== UiState.LocalImage && "hidden")}>
            <div className="relative h-[300px] w-full">
              <div className="absolute right-2 top-2 z-10">
                <IconButton Icon={TrashIcon} onClick={resetLocalImage} />
              </div>
              <Cropper
                image={localImage || ""}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                cropShape="round"
                showGrid={false}
              />
            </div>
          </div>
          <div
            className={clsx(
              "relative mx-auto my-10 flex size-[300px] flex-col items-center rounded-full",
              uiState !== UiState.ExistingImage && "hidden"
            )}>
            <div className="absolute right-0 top-0 z-10">
              <IconButton Icon={TrashIcon} onClick={handleRemoveExisting} />
            </div>
            <div
              className="relative h-full w-full cursor-pointer overflow-hidden rounded-full"
              onClick={() => hiddenInput.current?.click()}>
              <Image
                srcSetUrls={{
                  sm: existingImage!,
                }}
                alt="Cover image"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
          <div
            className={clsx(
              "mx-auto my-10 flex size-[300px] cursor-pointer items-center justify-center rounded-full border-2 border-bg-3",
              (uiState === UiState.ExistingImage || uiState === UiState.LocalImage) && "hidden"
            )}
            onClick={() => hiddenInput.current?.click()}>
            <PlusIcon className="size-[40px] stroke-fg-2" />
          </div>
          <input type="file" accept="image/*" className="hidden" ref={hiddenInput} onChange={handleImageChange} />
        </div>
      }
      footer={{
        fixed: true,
        tertiaryButton: {
          children: "Cancel",
          onClick: () => onOpenChange(false),
        },
        primaryButton: {
          children: uiState === UiState.ExistingImage ? "Change" : "Upload",
          onClick: uiState === UiState.ExistingImage ? () => hiddenInput.current?.click() : onDownloadCropClick,
          disabled: uiState === UiState.NoImage,
          loading: uploadLoading,
        },
      }}
    />
  )
}
